import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ClientLayout from "../../components/client/ClientLayout";
import { useAuth } from "../../context/AuthContext";
import { clientApi } from "../../api";
import { formatDateTimeAU } from "../../utils/formatDate";

export default function Dashboard() {
  const [summary, setSummary] = useState({
    totalMembers: 0,
    activeMembers: 0,
    expiringSoon: 0,
    joinedThisWeek: 0,
    revenue30dCents: 0,
    recentMembers: [],
    recentLogs: [],
  });
  const [loading, setLoading] = useState(true);
  const [venue, setVenue] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.clientRole === "admin";

  useEffect(() => {
    clientApi.getDashboardSummary()
      .then((s) => setSummary({ ...summary, ...s }))
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);
  useEffect(() => { clientApi.getMe().then((me) => setVenue(me?.venue || null)).catch(() => {}); }, []);

  const revenueDollars = (summary.revenue30dCents || 0) / 100;
  // Provider/feature gating for the quick actions, mirroring the sidebar so
  // an IGT venue never sees S7000-only actions (and vice versa). Both false
  // until /me resolves, so nothing provider-specific flashes on load.
  const provider = venue?.provider_type;
  const isIgt = provider === "igt";
  const isS7000 = !!venue && (provider === "s7000" || !provider);
  const digitalPassesOn = venue?.digital_passes_enabled !== false;

  return (
    <ClientLayout>
      <div className="venues-page-header">
        <div>
          <h1>Dashboard</h1>
          <p className="page-subtitle">Venue health at a glance</p>
        </div>
      </div>

      <div className="stat-grid">
        <Tile label="Total members" value={summary.totalMembers} sub={`${summary.activeMembers} active`} />
        <Tile label="Joined this week" value={summary.joinedThisWeek} sub="Last 7 days" color={summary.joinedThisWeek ? "var(--success)" : undefined} />
        <Tile label="Expiring in 30 days" value={summary.expiringSoon} color={summary.expiringSoon > 0 ? "var(--warning)" : undefined} linkTo="/client/reports/expiring" />
        <Tile label="Revenue (30d)" value={`$${revenueDollars.toFixed(2)}`} sub="Successful transactions" />
      </div>

      <div className="dashboard-cols">
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <h2 style={{ margin: 0 }}>Recent activity</h2>
            <Link to="/client/logs" style={{ fontSize: "0.85rem" }}>View all →</Link>
          </div>
          {loading ? <div style={{ color: "var(--text-muted)" }}>Loading…</div> : summary.recentLogs?.length ? (
            <div className="table-wrap" style={{ marginTop: "0.5rem" }}>
              <table className="data-table">
                <thead>
                  <tr><th>Date</th><th>Action</th><th>Member</th><th>Status</th><th>By</th></tr>
                </thead>
                <tbody>
                  {summary.recentLogs.map((l) => (
                    <tr key={l.id}>
                      <td style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>{formatDateTimeAU(l.created_at)}</td>
                      <td style={{ fontWeight: 500 }}>{l.action}</td>
                      <td>
                        {l.membership_id
                          ? <Link to={`/client/members/${l.membership_id}`}>{l.first_name || ""} {l.last_name || ""} <code style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>#{l.membership_id}</code></Link>
                          : "—"}
                      </td>
                      <td><span className={`badge ${l.status}`}>{l.status}</span></td>
                      <td style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>{l.actor_name || l.actor_email || (l.action === "join" ? "Public signup" : "—")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="empty-state"><p>No activity yet.</p></div>}
        </div>

        <div>
          <h2 style={{ marginTop: 0 }}>Quick actions</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            <Link className="btn btn-primary" to="/client/members">Look up a member</Link>
            {isS7000 && <Link className="btn btn-outline" to="/client/s7000/reset-pin">Reset a member's PIN</Link>}
            {isS7000 && <Link className="btn btn-outline" to="/client/s7000/pin-lookup">PIN-verified lookup</Link>}
            {isIgt && isAdmin && <Link className="btn btn-outline" to="/client/igt/change-pin">Change a member's PIN</Link>}
            {isIgt && isAdmin && <Link className="btn btn-outline" to="/client/igt/verify-pin">Verify a member's PIN</Link>}
            <Link className="btn btn-outline" to="/client/reports/expiring">Send renewal reminders</Link>
            {digitalPassesOn && <Link className="btn btn-outline" to="/client/passes">View issued passes</Link>}
          </div>

          <h3 style={{ marginTop: "2rem", fontSize: "1rem" }}>Recent signups</h3>
          {loading ? (
            <div style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>Loading…</div>
          ) : summary.recentMembers?.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              {summary.recentMembers.map((m) => (
                <Link key={m.id} to={`/client/members/${m.membership_id}`} className="list-row-link">
                  <div className="ct-avatar" style={{ width: 28, height: 28, fontSize: "0.72rem" }}>
                    {(m.first_name?.[0] || "").toUpperCase()}{(m.last_name?.[0] || "").toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "0.85rem", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.first_name} {m.last_name}</div>
                    <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>#{m.membership_id}</div>
                  </div>
                </Link>
              ))}
            </div>
          ) : <div style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>No members yet.</div>}
        </div>
      </div>
    </ClientLayout>
  );
}

function Tile({ label, value, sub, color, linkTo }) {
  const body = (
    <>
      <span className="stat-label">{label}</span>
      <span className="stat-value" style={{ color }}>{value}</span>
      {sub && <span className="stat-sub">{sub}</span>}
    </>
  );
  return linkTo ? (
    <Link to={linkTo} className="stat-card" style={{ textDecoration: "none", color: "inherit" }}>{body}</Link>
  ) : <div className="stat-card">{body}</div>;
}
